/**
 * Evidence Node Component
 *
 * Custom React Flow node for all evidence types in the Evidence Board.
 * Supports 12 node types for EU banking regulatory research.
 * Visual: White card with colored badge based on node_type
 */

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { WorkspaceNodeData, getNodeTypeIcon, getNodeTypeColor, getNodeTypeLabel } from '../types';
import { formatCount } from '../../../utils/plural';

function EvidenceNode({ data, selected }: NodeProps<WorkspaceNodeData>) {
  const { nodeType, label, relevanceNote, referenceIds } = data;
  const color = getNodeTypeColor(nodeType);
  const icon = getNodeTypeIcon(nodeType);
  const typeLabel = getNodeTypeLabel(nodeType);

  return (
    <div
      className={`
        bg-surface rounded-lg border-2 shadow-md
        transition-all duration-200
        min-w-[200px] max-w-[280px]
        ${selected ? 'ring-2 ring-accent ring-offset-2' : ''}
      `}
      style={{ borderColor: selected ? color : 'var(--border-default)' }}
    >
      {/* Input Handle (top) */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-fg-tertiary border-2 border-white"
      />

      {/* Header with badge */}
      <div
        className="px-3 py-2 rounded-t-md flex items-center gap-2"
        style={{ backgroundColor: `${color}15` }}
      >
        {/* Type Badge */}
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>

        {/* Type Label */}
        <span
          className="text-xs font-medium uppercase tracking-wide"
          style={{ color }}
        >
          {typeLabel}
        </span>

        {/* Reference Count */}
        {referenceIds.length > 0 && (
          <span className="ml-auto text-xs text-fg-tertiary bg-surface px-1.5 py-0.5 rounded">
            {formatCount(referenceIds.length, { one: 'ref', other: 'refs' })}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="px-3 py-2">
        {/* Label */}
        <h3 className="text-sm font-semibold text-fg mb-1 line-clamp-2">
          {label}
        </h3>

        {/* Relevance Note */}
        <p className="text-xs text-fg-secondary line-clamp-3">
          {relevanceNote}
        </p>
      </div>

      {/* Output Handle (bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 !bg-fg-tertiary border-2 border-white"
      />
    </div>
  );
}

export default memo(EvidenceNode);
